import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import BankAccountCards from './BankAccountCards';
import {
  getPaymentPolicy,
  hasUploadedSlip,
  hasVerifiedSlip,
  slipAllowsUpload,
  slipRequired,
} from '../../utils/paymentSlip';

const PaymentSlipSection = ({
  entity,
  policy: policyProp,
  advanceAmount,
  requiresAdvance,
  bankAccounts = [],
  salonMode,
  canManage = true,
  onUpload,
  onVerify,
  uploading = false,
}) => {
  const { t } = useTranslation();
  const policy = policyProp || getPaymentPolicy(entity);
  const slips = entity?.paymentSlips || [];
  const activeAccounts = bankAccounts.filter((a) => a.isActive !== false);
  const [selectedBankId, setSelectedBankId] = useState('');

  useEffect(() => {
    if (activeAccounts.length === 1) {
      setSelectedBankId(activeAccounts[0]._id);
    }
  }, [activeAccounts]);

  if (!slipAllowsUpload(policy) && !requiresAdvance) return null;

  const needsBankPick = !salonMode && activeAccounts.length > 1;
  const canUpload =
    !salonMode &&
    slipAllowsUpload(policy) &&
    onUpload &&
    (!needsBankPick || selectedBankId);

  const handleUpload = (file) => {
    const bankId = selectedBankId || activeAccounts[0]?._id || '';
    onUpload(file, bankId);
  };

  return (
    <div className="rounded-lg border border-zinc-200 dark:border-zinc-800 p-4">
      <p className="text-sm font-medium">{t('payments.title')}</p>
      <p className="mt-1 text-xs text-zinc-500">
        {t(`payments.policy.${policy.toLowerCase()}`)}
      </p>

      {requiresAdvance && advanceAmount > 0 && (
        <p className="mt-2 text-sm text-amber-300">
          {t('payments.advanceDue', { amount: Number(advanceAmount).toLocaleString() })}
        </p>
      )}

      {!salonMode && activeAccounts.length > 0 && (
        <div className="mt-3">
          <BankAccountCards
            accounts={activeAccounts}
            selectable={activeAccounts.length > 1}
            selectedId={selectedBankId}
            onSelect={setSelectedBankId}
          />
        </div>
      )}

      {!salonMode && !activeAccounts.length && (requiresAdvance || slipAllowsUpload(policy)) && (
        <p className="mt-2 text-xs text-amber-400">{t('payments.bank.notConfigured')}</p>
      )}

      {slipRequired(policy) && !hasUploadedSlip(entity) && (
        <p className="mt-2 text-xs text-amber-400">{t('payments.slipRequiredHint')}</p>
      )}

      {salonMode && slipRequired(policy) && !hasUploadedSlip(entity) && (
        <p className="mt-2 text-xs text-red-400">{t('payments.salonWaitingForSlip')}</p>
      )}

      {slips.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-3">
          {slips.map((slip, i) => (
            <div key={i} className="relative">
              <a href={slip.slipUrl} target="_blank" rel="noreferrer">
                <img src={slip.slipUrl} alt="" className="h-24 w-24 rounded-lg object-cover" />
              </a>
              {slip.bankName && (
                <p className="mt-1 max-w-24 text-[10px] text-zinc-500">
                  {t('payments.bank.transferredTo', { bank: slip.bankName })}
                </p>
              )}
              {slip.isVerified ? (
                <span className="mt-1 block text-xs text-emerald-400">{t('common.verified')}</span>
              ) : salonMode && canManage ? (
                <button
                  type="button"
                  onClick={() => onVerify?.(i)}
                  className="mt-1 text-xs text-glow-400 hover:underline"
                >
                  {t('common.verify')}
                </button>
              ) : (
                <span className="mt-1 block text-xs text-amber-400">{t('orders.drawer.pendingVerification')}</span>
              )}
            </div>
          ))}
        </div>
      )}

      {canUpload && (
        <label className="mt-3 inline-flex cursor-pointer rounded-lg border border-zinc-300 px-3 py-1.5 text-xs dark:border-zinc-700 hover:bg-zinc-100 dark:hover:bg-zinc-800">
          {uploading ? t('toast.uploadingSlip') : t('client.orders.uploadSlip')}
          <input
            type="file"
            accept="image/*"
            className="hidden"
            disabled={uploading}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handleUpload(file);
              e.target.value = '';
            }}
          />
        </label>
      )}

      {needsBankPick && !selectedBankId && slipAllowsUpload(policy) && (
        <p className="mt-2 text-xs text-amber-400">{t('payments.bank.selectBeforeUpload')}</p>
      )}

      {salonMode && hasUploadedSlip(entity) && !hasVerifiedSlip(entity) && (
        <p className="mt-2 text-xs text-zinc-500">{t('payments.reviewSlipHint')}</p>
      )}
    </div>
  );
};

export default PaymentSlipSection;
